import { IContextualMenuItem, Link, Stack, Text } from "@fluentui/react";
import { RootState } from "app/store";
import CustomPanel from "components/controls/CustomPanel";
import { EValidationSeverity, IValidationMessage } from "components/controls/ValidationView/ValidationView.type";
import { AppLinkTo } from "global/Application";
import { updateNotifications } from "global/Reducer/dataSlice";
import { SharedValidations } from "global/Validation/DataValidation";
import { EDataValidationContext } from "global/Validation/EDataValicationContext";
import { useMemo, useState } from "react";
import { ConnectedProps, connect, useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import ValidationItem from "views/BlueRelationship/ValidationDialog/ValidationItem";

interface IProps extends TComponentConnectedProps {
}

// TODO rename this to Action Items Panel
const ActionItemsPanel: React.FunctionComponent<IProps> = (props) => {
    const [refresh, SetRefresh] = useState(0);

    const state = useSelector((state: RootState) => state);
    const dispatch = useDispatch();

    const history = useHistory();

    const messages = useMemo(() => {
        const validationMessages: IValidationMessage[] = [];

        const dataValidationResult = SharedValidations(
            state,
            {
                checkRef: true,
                checkRelease: true,
            },
            EDataValidationContext.EActionItem,
            history
        );

        if (dataValidationResult.refValidations.length > 0) {
            validationMessages.push({
                title: `Some elements are referencing a resource that has been deactivated or no longer exists.`,
                description: `The occurrence of such a situation may arise when a resource undergoes deactivation during the course of business processing or fails to be selected for retention in the subsequent iteration of the merge process. In such cases, it is imperative to reassign or clear each individual entry.`,
                type: EValidationSeverity.error,
                inner: dataValidationResult.refValidations
            });
        }

        if (dataValidationResult.uniqueEntityNameValidations.length > 0) {
            const t = {
                title: `Some elements are sharing an identifier within the same context.`,
                description: `The following elements have been flagged due to naming errors, necessitating the assignment of unique names. To make the necessary changes, please navigate to the Properties section.\nHere is the list of impacted elements:`,
                type: EValidationSeverity.info,
                inner: dataValidationResult.uniqueEntityNameValidations
            };

            validationMessages.push(t);

            if (dataValidationResult.uniqueEntityNameValidations.some(t => t.type === EValidationSeverity.error)) {
                t.type = EValidationSeverity.error;
            }
        }

        if (dataValidationResult.releaseValidations.length > 0) {
            validationMessages.push(...dataValidationResult.releaseValidations);
        }




        // skip deactivated records/only show deactivated records if permitted at group config properties level


        const notifications: IValidationMessage[] = [];

        state.data.json.notifications?.forEach(eachNotification => {

            // displaying inactivated ones
            if ((eachNotification.deactivated ?? false) !== state.data.json.showInActives?.value)
                return;

            const validationMassage: IValidationMessage = {
                title: eachNotification.title ?? '',
                type: EValidationSeverity.info,
            }

            if (eachNotification.name === 'telehealth-deprecated-fields') {
                const benefit = state.data.json.benefits.find(t => t.id === eachNotification.location?.benefitId);
                // x.description = ;

                validationMassage.title = `Important Update on Telehealth Benefit (${benefit?.name})`;

                validationMassage.onRenderDescription = () => {
                    return <Stack tokens={{ childrenGap: '10px' }} styles={{ root: { padding: '5px', paddingTop: '20px' } }}>
                        <Stack.Item>
                            <Text>
                                Please be advised that attributes in the GENERAL MEDICINE / URGENT CARE, BEHAVIORAL HEALTH / MENTAL HEALTH, DERMATOLOGY, and VIRTUAL PRIMARY CARE sections of the Telehealth Benefit have been deprecated and replaced with a single costshare field.
                                <br />
                                Make sure to review your Telehealth Benefits and make any necessary updates accordingly.
                            </Text>
                        </Stack.Item>
                        <Stack.Item align="end">
                            <Stack horizontal tokens={{ childrenGap: '10px' }}>

                                {benefit &&
                                    <Text variant="small">
                                        <Link onClick={() => {
                                            history.push(AppLinkTo(state,
                                                {
                                                    id: benefit.id,
                                                    type: 'benefit',
                                                }
                                            ))
                                        }}>
                                            Benefit ({benefit.name})
                                        </Link>
                                    </Text>
                                }

                                <Text variant="small">
                                    <Link onClick={() => {
                                        dispatch(updateNotifications([{
                                            id: eachNotification.id,
                                            deactivated: !eachNotification.deactivated
                                        }]));
                                    }}>
                                        {eachNotification.deactivated ? "Activate" : "Deactivate"}
                                    </Link>
                                </Text>
                            </Stack>

                        </Stack.Item>
                    </Stack>
                }
            }

            if (eachNotification.name === 'retroactive-termination-claims-recoveries-general') {
                validationMassage.title = `Update: Retroactive Termination Claims Recovery Relocated to Shared Savings Line`;
                validationMassage.onRenderDescription = () => {
                    return <Stack tokens={{ childrenGap: '10px' }} styles={{ root: { padding: '5px', paddingTop: '20px' } }}>
                        <Stack.Item>
                            <Text>
                                Retroactive Termination Claims Recovery Percentage on the Accounts Receivable entity has been deprecated and the value has been moved to a Shared Savings line on the related Fees and Premium record.  Make sure to review the new line for correctness.
                            </Text>
                        </Stack.Item>
                        <Stack.Item align="end">
                            <Stack horizontal tokens={{ childrenGap: '10px' }}>

                                <Text variant="small">
                                    <Link onClick={() => {
                                        dispatch(updateNotifications([{
                                            id: eachNotification.id,
                                            deactivated: !eachNotification.deactivated
                                        }]));
                                    }}>
                                        {eachNotification.deactivated ? "Activate" : "Deactivate"}
                                    </Link>
                                </Text>
                            </Stack>

                        </Stack.Item>
                    </Stack>
                }
            }


            if (eachNotification.name === 'retroactive-termination-claims-recoveries-multiple-percentages') {
                const feesAndPremium = state.data.json.feesAndPremiums.find(t => t.id === eachNotification.location?.feesAndPremiumId);

                validationMassage.title = `Update: Multiple Shared Savings lines added to Fees and Premium.`;
                validationMassage.type = EValidationSeverity.warn;
                validationMassage.onRenderDescription = () => {
                    return <Stack tokens={{ childrenGap: '10px' }} styles={{ root: { padding: '5px', paddingTop: '20px' } }}>
                        <Stack.Item>
                            <Stack>
                                <Text>
                                    Multiple Retroactive Termination Claims Recovery lines have been added to the Fees and Premiums ({feesAndPremium?.name}) Shared Savings.
                                </Text>
                                <Text>
                                    We were expecting only a single line, but conflicting values were found in the source Accounts Receivable. As a result, we added them as new lines to the Shared Savings attribute set.
                                </Text>
                                <Text>
                                    Please review the new lines for correctness.
                                </Text>
                            </Stack>
                        </Stack.Item>
                        <Stack.Item align="end">
                            <Stack horizontal tokens={{ childrenGap: '10px' }}>
                                {feesAndPremium &&
                                    <Text variant="small">
                                        <Link onClick={() => {
                                            history.push(AppLinkTo(state,
                                                {
                                                    id: feesAndPremium.id,
                                                    type: 'feesAndPremium',
                                                }
                                            ))
                                        }}>
                                            Fees And Premium ({feesAndPremium.name})
                                        </Link>
                                    </Text>
                                }

                                <Text variant="small">
                                    <Link onClick={() => {
                                        dispatch(updateNotifications([{
                                            id: eachNotification.id,
                                            deactivated: !eachNotification.deactivated
                                        }]));
                                    }}>
                                        {eachNotification.deactivated ? "Activate" : "Deactivate"}
                                    </Link>
                                </Text>
                            </Stack>

                        </Stack.Item>
                    </Stack>
                }
            }

            if (eachNotification.name === 'sprint30-aba-therapy-oon-costshare') {
                validationMassage.title = `'ABA Therapy' has been consolidated as 'ABA Therapy - Out of Network' - please view the Mental Health Tab for updates.`;
                validationMassage.type = EValidationSeverity.info;
                validationMassage.onRenderDescription = () => {
                    return <Stack tokens={{ childrenGap: '10px' }} styles={{ root: { padding: '5px', paddingTop: '20px' } }}>
                        <Stack.Item>
                            <Stack>
                                <Text>
                                    Out Of Network tier value has been used from as 'ABA Therapy - Out of Network' - please view the Mental Health Tab for updates.
                                </Text>
                            </Stack>
                        </Stack.Item>
                        <Stack.Item align="end">
                            <Stack horizontal tokens={{ childrenGap: '10px' }}>
                                <Text variant="small">
                                    <Link onClick={() => {
                                        dispatch(updateNotifications([{
                                            id: eachNotification.id,
                                            deactivated: !eachNotification.deactivated
                                        }]));
                                    }}>
                                        {eachNotification.deactivated ? "Activate" : "Deactivate"}
                                    </Link>
                                </Text>
                            </Stack>

                        </Stack.Item>
                    </Stack>
                }
            }

            notifications.push(validationMassage);
        })


        if (notifications.length > 0)
            validationMessages.push(...notifications);

        return validationMessages;

    }, [refresh, state.data.json.notifications, state.data.json.showInActives?.value]);


    const menu: IContextualMenuItem[] = [];
    menu.push({
        key: 'refresh',
        name: 'Refresh',
        icon: 'Refresh',
        onClick: (event, item) => {
            SetRefresh(t => t + 1);
        },
    });

    if (messages.length < 1)
        return <></>;

    return <CustomPanel title={"Action Items"}
        canCollapse={false}
        defaultCollapsed={false}
        contentStyle={{ overflow: 'visible' }}
        storageId={"validationPanel"}
        items={menu}
    >
        <Stack styles={{ root: { marginTop: '10px' } }}>
            {messages.map((msg, ix) => {
                return <ValidationItem key={ix} data={msg} />;
            })}
        </Stack>
    </CustomPanel>;
}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(ActionItemsPanel)

